import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { candidateClosureUpdate } from "model/CandidateModel";
import { AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import { buildUserAsyncFilterInitialOption } from "helpers/metadataFilterHelpers";

/** Supports ISO datetimes from API e.g. `2026-04-30T00:00:00`. Falls back to today. */
const toDateInputValue = (raw) => {
  if (raw === null || raw === undefined || raw === "") {
    return dayjs().format("YYYY-MM-DD");
  }
  const d = dayjs(String(raw).trim());
  return d.isValid() ? d.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
};

/**
 * Same fields and PUT body as CandidateClosureModal (`Candidate/closure/{id}`).
 * Pre-fills user + closed date from the closure list row when editing.
 */
const ModifyClosureEditModal = ({
  isOpen,
  onClose,
  id,
  initialUserId = "",
  initialUserLabel = "",
  initialClosedDateRaw = "",
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState("");
  const [userLabel, setUserLabel] = useState("");
  const [closedDate, setClosedDate] = useState(() => dayjs().format("YYYY-MM-DD"));

  const userIdStr = String(initialUserId || "").trim();
  const userLabelStr = String(initialUserLabel || "").trim();

  useEffect(() => {
    if (!isOpen) return;
    setClosedDate(toDateInputValue(initialClosedDateRaw));
    setUserId(userIdStr);
    setUserLabel(userLabelStr || userIdStr);
  }, [isOpen, userIdStr, userLabelStr, initialClosedDateRaw]);

  const handleUserChange = (option) => {
    setUserId(option?.value ?? "");
    setUserLabel(option?.label ?? "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const itemid = String(id || "").trim();
    if (!itemid) {
      toast.error("ID not found");
      return;
    }
    const normalizedUserId = String(userId || "").trim();
    if (!normalizedUserId) {
      toast.error("Please select a user");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        userId: normalizedUserId,
        closeDate: closedDate || dayjs().format("YYYY-MM-DD"),
      };
      const response = await candidateClosureUpdate(id, payload);
      if (response?.success || response?.isSuccess) {
        toast.success(response?.message || "Closure updated");
        const closeDateIso = closedDate
          ? dayjs(closedDate).startOf("day").toISOString()
          : null;
        onSuccess?.({
          id: itemid,
          closeByUserId: normalizedUserId,
          userId: normalizedUserId,
          closeByUserName: userLabel || normalizedUserId,
          closeBy: userLabel || normalizedUserId,
          closeDateTime: closeDateIso,
          closeDate: closedDate || dayjs().format("YYYY-MM-DD"),
        });
        onClose?.();
      } else {
        toast.error(response?.message || "Unable to update closure");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    if (!submitting) onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      size="md"
      className="modal-full-height"
      centered
    >
      <Form onSubmit={handleSubmit}>
        <ModalBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold mb-0">Edit closure</h5>
            <Button close type="button" onClick={closeModal} disabled={submitting} />
          </div>
          <hr />

          {userLabelStr && !userIdStr ? (
            <p className="text-muted small mb-3">
              Closer on file: <strong>{userLabelStr}</strong>. Choose the same user below if the list did not return a
              user id.
            </p>
          ) : null}

          <FormGroup>
            <Label>Select user</Label>
            <AsyncUserSelect
              key={`modify-closure-edit-user-${id || "new"}-${isOpen}`}
              pageName="ModifyClosure"
              value={userId}
              initialOption={buildUserAsyncFilterInitialOption(userId, userLabel)}
              onChange={handleUserChange}
              placeholder="Select user"
            />
          </FormGroup>

          <FormGroup>
            <Label>Closed date</Label>
            <Input
              type="date"
              value={closedDate}
              onChange={(ev) => setClosedDate(ev.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Updating…
              </>
            ) : (
              "Update"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ModifyClosureEditModal;
